/**
 * Variant bottom sheet.
 *
 * Oversized options (more than six values) render as a native <select>, which on
 * mobile hands off to the OS picker -- a full-screen list with no product
 * context, no price and no image. This replaces that with a bottom sheet that
 * leaves the gallery visible and interactive in the upper half of the screen.
 *
 * Design notes:
 *
 * - No custom element. The variant picker morphs its own subtree on every
 *   change, so any per-element listener or attribute inside it can be swapped
 *   out mid-interaction. One delegated document listener and a class on <html>
 *   survive morphing; a connectedCallback would not re-fire on a reused node.
 *
 * - The <select> stays in the DOM and stays the source of truth. It is hidden
 *   only once this script has run (html.variant-sheet-ready), so if the module
 *   fails to load the native control is still there and the product is still
 *   buyable.
 *
 * - Preview is a real variant change dispatched on the select, which is what
 *   makes image, price and availability update for free through the theme's
 *   existing pipeline. Closing with X re-dispatches the original value, so the
 *   observable contract holds: nothing the shopper keeps unless they Confirm.
 *
 * - The page is NOT frozen. This is a Maps-style sheet: only the sheet's own
 *   list locks scrolling (overscroll-behavior), the root passes pointer events
 *   through, and there is no scrim. The gallery above stays swipeable,
 *   zoomable and scrollable while the sheet is open.
 */
(function () {
  var MOBILE = window.matchMedia('(max-width: 749px)');
  var READY_CLASS = 'variant-sheet-ready';
  var ANIMATION_MS = 280;
  var SHEET_FRACTION = 0.5; // panel is max-height: 50dvh
  var EDGE_GAP = 8;
  var SCROLL_CEILING_MS = 700;

  /** @type {HTMLElement | null} */
  var openRoot = null;
  /** @type {{ selectId: string, originalValue: string, trigger: HTMLElement | null, onResize: (() => void) | null } | null} */
  var openState = null;

  /**
   * The chevron is a first-run hint, not a persistent control. Once the shopper
   * has scrolled a sheet even once they have learned the gesture, so it stays
   * gone for the rest of the page session rather than reappearing on every
   * option they open.
   */
  var hintDismissed = false;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function syncReady() {
    document.documentElement.classList.toggle(READY_CLASS, MOBILE.matches);
    // Rotating to landscape past the breakpoint hands control back to the
    // native select, so the sheet must not be left hanging over it.
    if (!MOBILE.matches && openRoot) dismiss(false);
  }

  function getSelect(id) {
    var element = document.getElementById(id);
    return element instanceof HTMLSelectElement ? element : null;
  }

  /**
   * Applies a value to the select and lets the theme's variant picker react.
   * Re-queried by id every time: the picker morphs its subtree after each
   * change, so a cached node reference can go stale.
   */
  function applyValue(selectId, value) {
    var select = getSelect(selectId);
    if (!select || select.value === value) return;
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------------------------------------------------------------------- *
   * Bringing the gallery into view
   * ---------------------------------------------------------------------- */

  function findGallery() {
    return (
      document.querySelector('.product-information__media') ||
      document.querySelector('media-gallery') ||
      document.querySelector('.product-media-container')
    );
  }

  function headerOffset() {
    var raw = getComputedStyle(document.body).getPropertyValue('--header-height');
    var parsed = parseFloat(raw);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Waits until scrolling has actually settled.
   *
   * scrollend is not available everywhere yet, and a fixed delay is either too
   * short on a long smooth scroll or a stall on a short one, so this polls for
   * a stable scroll position with a hard ceiling as a backstop.
   *
   * @param {() => void} done
   */
  function afterScrollSettles(done) {
    var finished = false;
    var last = window.scrollY;
    var stableFrames = 0;

    function finish() {
      if (finished) return;
      finished = true;
      window.removeEventListener('scrollend', finish);
      window.clearTimeout(ceiling);
      window.clearInterval(poll);
      done();
    }

    var ceiling = window.setTimeout(finish, SCROLL_CEILING_MS);
    var poll = window.setInterval(function () {
      if (Math.abs(window.scrollY - last) < 1) {
        stableFrames += 1;
        if (stableFrames > 2) finish();
      } else {
        stableFrames = 0;
      }
      last = window.scrollY;
    }, 60);

    if ('onscrollend' in window) window.addEventListener('scrollend', finish);
  }

  /**
   * Scrolls so the gallery sits in the strip left above the sheet.
   *
   * Without this the sheet opens over whatever the shopper happened to be
   * looking at -- usually the title or the description -- which makes a live
   * preview of the image pointless. The gallery is centred in the free space
   * when it fits, and pinned under the header when it is taller than the strip.
   *
   * @param {() => void} done - Called once the page has settled.
   */
  function revealGallery(done) {
    var gallery = findGallery();
    if (!gallery) return done();

    var viewport = window.innerHeight;
    var top = headerOffset() + EDGE_GAP;
    var bottom = viewport - viewport * SHEET_FRACTION - EDGE_GAP;
    var available = bottom - top;
    if (available <= 0) return done();

    var rect = gallery.getBoundingClientRect();
    var desiredTop = rect.height <= available ? top + (available - rect.height) / 2 : top;
    var delta = Math.round(rect.top - desiredTop);

    if (Math.abs(delta) < 2) return done();

    if (prefersReducedMotion()) {
      window.scrollBy(0, delta);
      return done();
    }

    window.scrollBy({ top: delta, left: 0, behavior: 'smooth' });
    afterScrollSettles(done);
  }

  /* ---------------------------------------------------------------------- *
   * Scroll affordance
   * ---------------------------------------------------------------------- */

  /**
   * Drives the fade and the chevron from the list's real scroll geometry.
   *
   * Both are pure CSS states on the wrapper: the fade shows while the list can
   * scroll and hides once the end is reached, so it never sits over the last
   * row pretending there is more.
   *
   * @param {HTMLElement} scroller
   * @param {HTMLElement} list
   */
  function syncScrollAffordance(scroller, list) {
    var scrollable = list.scrollHeight - list.clientHeight > 1;

    if (scrollable) {
      scroller.dataset.scrollable = 'true';
    } else {
      delete scroller.dataset.scrollable;
    }

    var atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;

    if (scrollable && !atBottom) {
      delete scroller.dataset.atBottom;
    } else {
      scroller.dataset.atBottom = 'true';
    }
  }

  /* ---------------------------------------------------------------------- *
   * Sheet
   * ---------------------------------------------------------------------- */

  function buildOption(item, isSelected) {
    var listItem = document.createElement('li');
    listItem.className = 'variant-sheet__item';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'variant-sheet__option';
    button.dataset.value = item.value;
    button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    if (!item.available) button.dataset.unavailable = 'true';

    // Colour values carry their muted tint through the same custom properties
    // the button pills use, so the pressed row shows the real (softened)
    // colour and no second colour system exists to drift out of sync.
    if (item.chipBackground) {
      button.style.setProperty('--color-selected-variant-background', item.chipBackground);
      button.style.setProperty('--color-selected-variant-border', item.chipBorder || item.chipBackground);
      if (item.chipText) button.style.setProperty('--color-selected-variant-text', item.chipText);
    }

    var label = document.createElement('span');
    label.className = 'variant-sheet__option-label';
    label.textContent = item.label;
    button.appendChild(label);

    if (!item.available) {
      var note = document.createElement('span');
      note.className = 'variant-sheet__option-note';
      note.textContent = item.unavailableLabel;
      button.appendChild(note);
    }

    listItem.appendChild(button);
    return listItem;
  }

  function open(host) {
    if (openRoot) return;

    var selectId = host.dataset.selectId || '';
    var select = getSelect(selectId);
    if (!select) return;

    var unavailableLabel = host.dataset.unavailableLabel || '';
    var items = Array.prototype.map.call(select.options, function (option) {
      return {
        value: option.value,
        label: option.dataset.label || option.textContent.trim(),
        available: option.dataset.optionAvailable !== 'false',
        chipBackground: option.dataset.chipBackground || '',
        chipText: option.dataset.chipText || '',
        chipBorder: option.dataset.chipBorder || '',
        unavailableLabel: unavailableLabel,
      };
    });

    openState = {
      selectId: selectId,
      originalValue: select.value,
      trigger: host.querySelector('[data-variant-sheet-trigger]'),
      onResize: null,
    };

    var root = document.createElement('div');
    root.className = 'variant-sheet__root';

    var panel = document.createElement('div');
    panel.className = 'variant-sheet__panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', host.dataset.optionName || '');
    panel.tabIndex = -1;

    var header = document.createElement('div');
    header.className = 'variant-sheet__header';

    var title = document.createElement('span');
    title.className = 'variant-sheet__title';
    title.textContent = host.dataset.optionName || '';
    header.appendChild(title);

    var closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'variant-sheet__close';
    closeButton.dataset.variantSheetDismiss = 'true';
    closeButton.setAttribute('aria-label', host.dataset.closeLabel || 'Close');
    closeButton.innerHTML =
      '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">' +
      '<path d="M5 5 L15 15 M15 5 L5 15" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>' +
      '</svg>';
    header.appendChild(closeButton);
    panel.appendChild(header);

    // The fade has to be a sibling of the list, not a child: inside the
    // scrolling element it would scroll away with the options.
    var scroller = document.createElement('div');
    scroller.className = 'variant-sheet__scroller';

    var list = document.createElement('ul');
    list.className = 'variant-sheet__list';
    list.setAttribute('role', 'list');
    items.forEach(function (item) {
      list.appendChild(buildOption(item, item.value === openState.originalValue));
    });
    scroller.appendChild(list);

    var fade = document.createElement('div');
    fade.className = 'variant-sheet__fade';
    fade.setAttribute('aria-hidden', 'true');
    scroller.appendChild(fade);

    if (!hintDismissed) {
      var hint = document.createElement('span');
      hint.className = 'variant-sheet__hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.innerHTML =
        '<svg viewBox="0 0 20 12" width="18" height="11" aria-hidden="true" focusable="false">' +
        '<path d="M2 2 L10 10 L18 2" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';
      scroller.appendChild(hint);
    }

    panel.appendChild(scroller);

    var footer = document.createElement('div');
    footer.className = 'variant-sheet__footer';

    var confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'button variant-sheet__confirm';
    confirm.dataset.variantSheetConfirm = 'true';
    confirm.textContent = host.dataset.confirmLabel || 'Confirm';
    footer.appendChild(confirm);
    panel.appendChild(footer);

    root.appendChild(panel);
    document.body.appendChild(root);

    openRoot = root;
    document.addEventListener('keydown', onKeydown);

    // Measured after mount: the panel is laid out even while parked under its
    // transform, so the geometry is already correct here.
    syncScrollAffordance(scroller, list);

    list.addEventListener(
      'scroll',
      function () {
        if (!hintDismissed) {
          hintDismissed = true;
          scroller.dataset.hintDismissed = 'true';
        }
        syncScrollAffordance(scroller, list);
      },
      { passive: true }
    );

    // Rotation changes how many rows fit, so a list that was not scrollable can
    // become scrollable without the content changing.
    openState.onResize = function () {
      syncScrollAffordance(scroller, list);
    };
    window.addEventListener('resize', openState.onResize);

    // The panel is parked off-screen under a transform, so it costs nothing to
    // mount it before the scroll. Only the animation waits.
    revealGallery(function () {
      if (openRoot !== root) return;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (openRoot === root) {
            root.dataset.open = 'true';
            panel.focus({ preventScroll: true });
          }
        });
      });
    });
  }

  function teardown() {
    var root = openRoot;
    var trigger = openState && openState.trigger;
    var onResize = openState && openState.onResize;

    openRoot = null;
    openState = null;

    document.removeEventListener('keydown', onKeydown);
    if (onResize) window.removeEventListener('resize', onResize);

    if (!root) return;
    delete root.dataset.open;

    var remove = function () {
      if (root && root.parentNode) root.parentNode.removeChild(root);
    };

    if (prefersReducedMotion()) {
      remove();
    } else {
      window.setTimeout(remove, ANIMATION_MS);
    }

    // The trigger may have been replaced by a morph while the sheet was open,
    // so only restore focus if the node we captured is still on the page.
    if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
  }

  /**
   * @param {boolean} commit - true to keep the previewed variant, false to put
   *   the original one back before closing.
   */
  function dismiss(commit) {
    if (!openRoot || !openState) return;
    var state = openState;
    if (!commit) applyValue(state.selectId, state.originalValue);
    teardown();
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      dismiss(false);
    }
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var trigger = target.closest('[data-variant-sheet-trigger]');
    if (trigger) {
      if (!MOBILE.matches) return;
      event.preventDefault();
      var host = trigger.closest('[data-variant-sheet]');
      if (host instanceof HTMLElement) open(host);
      return;
    }

    if (!openRoot) return;

    if (target.closest('[data-variant-sheet-confirm]')) {
      event.preventDefault();
      dismiss(true);
      return;
    }

    if (target.closest('[data-variant-sheet-dismiss]')) {
      event.preventDefault();
      dismiss(false);
      return;
    }

    var option = target.closest('.variant-sheet__option');
    if (option instanceof HTMLElement && openRoot.contains(option)) {
      event.preventDefault();
      if (option.dataset.unavailable === 'true') return;

      var value = option.dataset.value || '';
      var buttons = openRoot.querySelectorAll('.variant-sheet__option');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute('aria-pressed', buttons[i] === option ? 'true' : 'false');
      }

      if (openState) applyValue(openState.selectId, value);
    }
  });

  syncReady();
  if (typeof MOBILE.addEventListener === 'function') MOBILE.addEventListener('change', syncReady);
})();
